/* eslint-disable complexity */

"use client";

import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowLeft } from "react-icons/fi";

import { Button } from "../atoms/Button";
import { Card } from "../atoms/Card";
import SkeletonLoader from "../molecules/SkeletonLoader";
import { ActionInputCard } from "../organisms/ActionInputCard";
import { useWalletInfo } from "contexts/WalletInfoContext";
import { useLiquidityAction } from "hooks/useLiquidityActions";
import { useMarinadeState } from "hooks/useMarinadeState";
import Images from "styles/image-library";
import { PositionType } from "types/wallet-position";
import { format2Dec, format4Dec } from "utils/number-to-short-version";

interface ActionLiquidityInputScreenProps {
  className?: string;
  action: "deposit" | "withdraw";
}

export const ActionLiquidityInputScreen: FC<
  ActionLiquidityInputScreenProps
> = ({ className, action }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isLoaded } = useWalletInfo();
  const marinadeState = useMarinadeState();

  const [inputIcon, setInputIcon] = useState<string>();
  const [inputTitle, setInputTitle] = useState<string>();
  const [inputSymbol, setInputSymbol] = useState<string>();
  const [inputValue, setInputValue] = useState<number>(0);

  const {
    balance,
    isLoading: isTxLoading,
    lpAmountToReceive,
    solAmountToReceive,
    mSolAmountToReceive,
    execute,
    setInputAmount,
  } = useLiquidityAction(action);

  const getButtonLabel = () => {
    if (balance && inputValue > balance) return t("app.insufficient-balance");
    if (action === "deposit") return t("liquidityPage.deposit");
    return t("liquidityPage.withdraw");
  };

  useEffect(() => {
    if (action === "withdraw") {
      setInputIcon(Images.icons_mSOL_SOL_LP);
      setInputTitle("LP");
      setInputSymbol("LP");
    } else {
      setInputIcon(Images.icons_solana_dark);
      setInputTitle("Solana");
      setInputSymbol("SOL");
    }
  }, [action]);

  useEffect(() => {
    if (balance && inputValue > balance) {
      setInputAmount(0);
    } else {
      setInputAmount(inputValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, balance]);

  const isLoading = !inputTitle || !inputSymbol || !isLoaded;
  const apy = marinadeState?.liquidityPool.apy.year;

  return (
    <div
      className={`absolute min-h-screen top-0 left-0 z-[49] bg-page-bg w-full md:relative md:min-h-full md:z-0 flex flex-col px-2 md:max-w-xl items-center ${className}`}
    >
      <div className="py-3 px-6 flex w-full font-medium text-2xl md:text-4xl md:px-0 gap-3.5 items-center justify-start">
        <motion.button
          whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
          onClick={() => router.push("/liquidity/")}
        >
          <FiArrowLeft className="h-7 w-7 text-primary" />
        </motion.button>
        <span className="flex-grow">
          {action === "deposit"
            ? t("liquidityPage.deposit-sol")
            : t("liquidityPage.withdraw-sol")}
        </span>
      </div>
      <div className="p-2 pt-0 w-full pb-[86px] md:pb-0">
        <ActionInputCard
          isLoading={isLoading}
          inputIcon={inputIcon}
          inputTitle={inputTitle}
          inputBalance={balance ?? 0}
          inputType={action === "deposit" ? PositionType.Raw : PositionType.LP}
          onInputChange={(v) => setInputValue(v)}
        />
        <Card
          className={`flex-col gap-4 font-medium mt-2 ${
            balance && inputValue > balance ? "hidden" : "flex"
          }`}
        >
          {action === "deposit" && (
            <div className="flex justify-between items-start">
              APY
              <SkeletonLoader isLoading={!apy} className="h-6 w-16">
                <span className="text-primary">
                  {format2Dec((apy ?? 0) * 100)}%
                </span>
              </SkeletonLoader>
            </div>
          )}
          <div className="flex justify-between items-start">
            You receive
            <div className="font-normal text-right">
              {action === "deposit" && (
                <SkeletonLoader
                  isLoading={
                    isLoading ||
                    isTxLoading ||
                    typeof lpAmountToReceive !== "number"
                  }
                  className="h-6 w-28"
                >
                  <span>{format4Dec(lpAmountToReceive ?? 0)} LP</span>
                </SkeletonLoader>
              )}
              {action === "withdraw" && (
                <>
                  <SkeletonLoader
                    isLoading={
                      isLoading ||
                      isTxLoading ||
                      typeof solAmountToReceive !== "number"
                    }
                    className="h-[22px] w-28 mb-1"
                  >
                    <span>{format4Dec(solAmountToReceive ?? 0)} SOL</span>
                  </SkeletonLoader>
                  <SkeletonLoader
                    isLoading={
                      isLoading ||
                      isTxLoading ||
                      typeof mSolAmountToReceive !== "number"
                    }
                    className="h-[22px] w-28"
                  >
                    <span className="block">
                      {format4Dec(mSolAmountToReceive ?? 0)} mSOL
                    </span>
                  </SkeletonLoader>
                </>
              )}
            </div>
          </div>
        </Card>
        <Button
          variant="primary"
          label={getButtonLabel()}
          className="w-full mt-4"
          onClick={() => execute(() => router.push("/liquidity/"))}
          isLoading={isTxLoading}
          disabled={
            (!(solAmountToReceive || mSolAmountToReceive) &&
              !lpAmountToReceive) ||
            (balance && inputValue > balance) ||
            isLoading ||
            isTxLoading
          }
        />
      </div>
    </div>
  );
};
